// @ts-nocheck
import { useState, useRef, useCallback, useEffect, lazy, Suspense, useMemo } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AI_AGENT_USER_ID } from "@shared/schema";
import { Bot, Smile, FileText, Send, Paperclip, Mic, MicOff } from "lucide-react";
import { ChatFilePreview } from "./chat-file-preview";
import { useToast } from "@/hooks/use-toast";

function getSpeechRecognitionAPI() {
  if (typeof window === "undefined") return undefined;
  return window.SpeechRecognition || window.webkitSpeechRecognition || undefined;
}

// Lazy-load emoji picker to keep it out of the initial bundle
const EmojiPickerInner = lazy(async () => {
  const [{ default: Picker }, { default: data }] = await Promise.all([
    import('@emoji-mart/react'),
    import('@emoji-mart/data'),
  ]);
  return {
    default: ({ onSelect }: { onSelect: (native: string) => void }) => (
      <Picker
        data={data}
        onEmojiSelect={(e: { native: string }) => onSelect(e.native)}
        theme="auto"
        previewPosition="none"
        skinTonePosition="none"
      />
    ),
  };
});

function EmojiPicker({ onSelect }: { onSelect: (native: string) => void }) {
  return <EmojiPickerInner onSelect={onSelect} />;
}

interface Mention {
  userId?: string;
  agentId?: string;
  claimId?: string;
  offset: number;
  length: number;
}

interface Participant {
  userId: string;
  subtitle?: string;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface ClaimResult {
  id: string;
  claimNumber: string;
  policyholderFirstName: string | null;
  policyholderLastName: string | null;
}

interface MentionInputProps {
  participants: Participant[];
  agents?: Array<{ id: string; displayName: string }>;
  onSend: (data: { text: string; mentions: Mention[]; files?: File[] }) => void;
  placeholder?: string;
  disabled?: boolean;
  sentHistory?: string[];
  resizable?: boolean;
  enableClaimMention?: boolean;
  maxFiles?: number;
}

export function MentionInput({
  participants,
  agents,
  onSend,
  placeholder = "Type your message...",
  disabled,
  sentHistory,
  resizable,
  enableClaimMention,
  maxFiles,
}: MentionInputProps) {
  const { toast } = useToast();
  // Evaluate once per mount so module caching does not break test isolation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const SpeechRecognitionAPI = useMemo(() => getSpeechRecognitionAPI(), []);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteFilter, setAutocompleteFilter] = useState("");
  const [triggerIndex, setTriggerIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [textareaHeight, setTextareaHeight] = useState(38);
  const [claimResults, setClaimResults] = useState<ClaimResult[]>([]);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftRef = useRef("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);

  const autoGrow = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!resizable) {
      autoGrow();
    }
  }, [text, resizable, autoGrow]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  // Build user list: participants + agents
  const allUsers = [
    ...participants
      .filter(p => p.user)
      .map(p => ({
        userId: p.userId,
        name: `${p.user?.firstName || ''} ${p.user?.lastName || ''}`.trim() || p.user?.email || 'Unknown',
        isBot: p.userId === AI_AGENT_USER_ID,
        agentId: undefined as string | undefined,
        subtitle: p.subtitle,
      })),
    // Include configured agents
    ...(agents || []).map(a => ({
      userId: a.id,
      name: a.displayName,
      isBot: true,
      agentId: a.id,
    })),
    // Legacy: always include AI agent if not already in participants and no agents provided
    ...((!agents || agents.length === 0) && !participants.some(p => p.userId === AI_AGENT_USER_ID)
      ? [{ userId: AI_AGENT_USER_ID, name: 'AI Agent', isBot: true, agentId: undefined as string | undefined }]
      : []),
  ];

  const filteredUsers = allUsers.filter(u =>
    u.name.toLowerCase().includes(autocompleteFilter.toLowerCase())
  );

  // Debounced claim search
  useEffect(() => {
    if (!enableClaimMention || !showAutocomplete || autocompleteFilter.length < 2) {
      setClaimResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/claims?limit=5&search=${encodeURIComponent(autocompleteFilter)}`);
        if (res.ok) {
          const data = await res.json();
          setClaimResults(data.claims ?? []);
        }
      } catch {
        setClaimResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [enableClaimMention, showAutocomplete, autocompleteFilter]);

  const totalAutocompleteCount = filteredUsers.length + claimResults.length;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).slice(0, (maxFiles ?? 10) - stagedFiles.length);
    if (droppedFiles.length > 0) setStagedFiles(prev => [...prev, ...droppedFiles].slice(0, maxFiles ?? 10));
  }, [stagedFiles.length, maxFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []).slice(0, (maxFiles ?? 10) - stagedFiles.length);
    if (selected.length > 0) setStagedFiles(prev => [...prev, ...selected].slice(0, maxFiles ?? 10));
    e.target.value = '';
  }, [stagedFiles.length, maxFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imageFiles = Array.from(e.clipboardData?.items ?? [])
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item, index) => {
        const raw = item.getAsFile();
        if (!raw) return null;
        if (!raw.name) {
          const ext = item.type.split('/')[1] ?? 'png';
          return new File([raw], `pasted-image-${Date.now() + index}.${ext}`, { type: item.type });
        }
        return raw;
      })
      .filter((f): f is File => f !== null);
    if (!imageFiles.length) return;
    const limit = maxFiles ?? 10;
    const currentCount = stagedFiles.length;
    const slotsAvailable = limit - currentCount;

    if (slotsAvailable <= 0) {
      toast({ variant: "destructive", title: "File limit reached", description: `Maximum ${limit} files allowed.` });
      return;
    }

    const accepted = imageFiles.slice(0, slotsAvailable);
    const dropped = imageFiles.length - accepted.length;

    if (dropped > 0) {
      toast({ variant: "destructive", title: "Some images not added", description: `${dropped} image${dropped > 1 ? 's' : ''} not added — maximum ${limit} files allowed.` });
    }

    setStagedFiles(prev => [...prev, ...accepted]);
  }, [maxFiles, stagedFiles.length, toast]);

  const removeFile = useCallback((index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart ?? value.length;
    setText(value);
    // Auto-grow after state update
    requestAnimationFrame(() => autoGrow());

    // Check for @ trigger
    const textBeforeCursor = value.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex >= 0) {
      const charBefore = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);

      if ((charBefore === ' ' || charBefore === '\n' || lastAtIndex === 0) && !/\s/.test(textAfterAt)) {
        setShowAutocomplete(true);
        setAutocompleteFilter(textAfterAt);
        setTriggerIndex(lastAtIndex);
        setSelectedIndex(0);
        return;
      }
    }

    setShowAutocomplete(false);
  }, [autoGrow]);

  const insertMention = useCallback((user: typeof allUsers[0]) => {
    const mentionText = `@${user.name}`;
    const before = text.slice(0, triggerIndex);
    const after = text.slice(triggerIndex + 1 + autocompleteFilter.length);
    const newText = before + mentionText + ' ' + after;

    const newMention: Mention = {
      userId: user.userId,
      ...(user.agentId ? { agentId: user.agentId } : {}),
      offset: triggerIndex,
      length: mentionText.length,
    };

    const offsetDiff = mentionText.length + 1 - (1 + autocompleteFilter.length);
    const adjustedMentions = mentions.map(m =>
      m.offset > triggerIndex ? { ...m, offset: m.offset + offsetDiff } : m
    );

    setText(newText);
    setMentions([...adjustedMentions, newMention]);
    setShowAutocomplete(false);
    setAutocompleteFilter("");
    setTriggerIndex(-1);

    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [text, mentions, triggerIndex, autocompleteFilter]);

  const insertClaimMention = useCallback((claim: ClaimResult) => {
    const name = [claim.policyholderFirstName, claim.policyholderLastName]
      .filter(Boolean)
      .join(' ') || `#${claim.claimNumber}`;
    const mentionText = `@${name}`;
    const before = text.slice(0, triggerIndex);
    const after = text.slice(triggerIndex + 1 + autocompleteFilter.length);
    const newText = before + mentionText + ' ' + after;

    const newMention: Mention = {
      claimId: claim.id,
      offset: triggerIndex,
      length: mentionText.length,
    };

    const offsetDiff = mentionText.length + 1 - (1 + autocompleteFilter.length);
    const adjustedMentions = mentions.map(m =>
      m.offset > triggerIndex ? { ...m, offset: m.offset + offsetDiff } : m
    );

    setText(newText);
    setMentions([...adjustedMentions, newMention]);
    setShowAutocomplete(false);
    setAutocompleteFilter('');
    setTriggerIndex(-1);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [text, mentions, triggerIndex, autocompleteFilter]);

  const insertEmoji = useCallback((native: string) => {
    const pos = textareaRef.current?.selectionStart ?? text.length;
    const newText = text.slice(0, pos) + native + text.slice(pos);
    setText(newText);
    setMentions(prev =>
      prev.map(m => (m.offset >= pos ? { ...m, offset: m.offset + native.length } : m))
    );
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newPos = pos + native.length;
        textareaRef.current.selectionStart = newPos;
        textareaRef.current.selectionEnd = newPos;
      }
    }, 0);
  }, [text]);

  const toggleVoiceInput = useCallback(() => {
    if (!SpeechRecognitionAPI) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setInterimTranscript("");
      return;
    }

    // Null out handlers on any old instance before creating a new one (fix handler leak)
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      setInterimTranscript(interim);
      if (final) {
        // Read selectionStart at event time to avoid stale closure; use functional update for text
        const pos = textareaRef.current?.selectionStart ?? 0;
        setText(prev => {
          const prefix = prev.length > 0 && !prev.endsWith(" ") ? " " : "";
          const insertion = prefix + final;
          const newText = prev.slice(0, pos) + insertion + prev.slice(pos);
          setMentions(prevMentions =>
            prevMentions.map(m => (m.offset >= pos ? { ...m, offset: m.offset + insertion.length } : m))
          );
          setInterimTranscript("");
          requestAnimationFrame(() => autoGrow());
          setTimeout(() => {
            if (textareaRef.current) {
              const newPos = pos + insertion.length;
              textareaRef.current.selectionStart = newPos;
              textareaRef.current.selectionEnd = newPos;
              textareaRef.current.focus();
            }
          }, 0);
          return newText;
        });
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed") {
        toast({
          variant: "destructive",
          title: "Microphone access denied",
          description: "Allow microphone access in your browser settings to use voice input.",
        });
      } else if (event.error === "audio-capture") {
        toast({
          variant: "destructive",
          title: "No microphone detected",
          description: "Make sure a microphone is connected and enabled.",
        });
      }
      // no-speech and other non-fatal errors reset silently
      setIsListening(false);
      setInterimTranscript("");
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript("");
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, toast, autoGrow]);

  const handleSend = useCallback(() => {
    if (text.trim() || stagedFiles.length > 0) {
      // Stop recognition before clearing text to avoid stale state
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      setInterimTranscript("");
      onSend({ text: text.trim(), mentions, files: stagedFiles.length > 0 ? stagedFiles : undefined });
      setText("");
      setMentions([]);
      setStagedFiles([]);
      setHistoryIndex(-1);
      draftRef.current = "";
      requestAnimationFrame(() => autoGrow());
    }
  }, [text, mentions, stagedFiles, onSend, autoGrow]);

  const handleDragMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      startY: e.clientY,
      startH: textareaRef.current?.offsetHeight ?? textareaHeight,
    };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      // Drag up = larger textarea (handle sits above the textarea)
      const dy = dragRef.current.startY - ev.clientY;
      const newH = Math.max(38, Math.min(200, dragRef.current.startH + dy));
      setTextareaHeight(newH);
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [textareaHeight]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showAutocomplete) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalAutocompleteCount - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (selectedIndex < filteredUsers.length) {
          if (filteredUsers[selectedIndex]) {
            insertMention(filteredUsers[selectedIndex]);
          }
        } else {
          const claimIdx = selectedIndex - filteredUsers.length;
          if (claimResults[claimIdx]) {
            insertClaimMention(claimResults[claimIdx]);
          }
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowAutocomplete(false);
        return;
      }
    }

    if (e.key === 'ArrowUp' && (!text || historyIndex !== -1) && sentHistory?.length) {
      e.preventDefault();
      const newIndex = historyIndex === -1 ? sentHistory.length - 1 : Math.max(historyIndex - 1, 0);
      if (historyIndex === -1) draftRef.current = text;
      setHistoryIndex(newIndex);
      setText(sentHistory[newIndex]);
      return;
    }
    if (e.key === 'ArrowDown' && historyIndex !== -1) {
      e.preventDefault();
      if (historyIndex >= sentHistory!.length - 1) {
        setHistoryIndex(-1);
        setText(draftRef.current);
      } else {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setText(sentHistory![newIndex]);
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (text.trim() || stagedFiles.length > 0) {
        // Stop recognition before clearing text
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        setIsListening(false);
        setInterimTranscript("");
        onSend({ text: text.trim(), mentions, files: stagedFiles.length > 0 ? stagedFiles : undefined });
        setText("");
        setMentions([]);
        setStagedFiles([]);
        setHistoryIndex(-1);
        draftRef.current = "";
        requestAnimationFrame(() => autoGrow());
      }
    }
  }, [showAutocomplete, filteredUsers, claimResults, selectedIndex, totalAutocompleteCount, insertMention, insertClaimMention, text, mentions, stagedFiles, onSend, sentHistory, historyIndex, autoGrow]);

  return (
    <div
      className={`relative w-full ${dragOver ? 'ring-2 ring-primary ring-offset-1 rounded-md' : ''}`}
      data-testid="mention-input-container"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag handle — only shown when resizable */}
      {resizable && (
        <div
          className="h-2 cursor-ns-resize flex items-center justify-center hover:bg-muted/50 rounded-t select-none"
          onMouseDown={handleDragMouseDown}
          data-testid="textarea-resize-handle"
        >
          <div className="w-8 h-0.5 bg-border rounded-full" />
        </div>
      )}

      <div className="relative">
        {mentions.length > 0 && (
          <div className="flex flex-wrap gap-1 px-2 pt-1.5 pb-0.5">
            {mentions.map((m, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 bg-primary/15 text-primary text-xs font-medium rounded-full px-2 py-0.5"
              >
                {text.slice(m.offset, m.offset + m.length)}
              </span>
            ))}
          </div>
        )}

        {/* Staged file previews */}
        <ChatFilePreview files={stagedFiles} onRemove={removeFile} />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          data-testid="file-input"
        />

        {/* Interim transcript preview — shown while listening, inside the relative wrapper */}
        {interimTranscript && (
          <div className="px-2 pt-1 pb-0 text-sm text-muted-foreground italic truncate">
            {interimTranscript}
          </div>
        )}

        {/* Input row: file picker + mic button (when supported) + textarea + emoji + send */}
        <div className="flex items-end gap-1.5">
          {/* File picker button — unchanged */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || stagedFiles.length >= (maxFiles ?? 10)}
            className="shrink-0 h-[38px] w-9 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="file-picker-button"
          >
            <Paperclip className="h-4 w-4" />
          </button>

          {/* Mic button — hidden entirely when SpeechRecognition unsupported */}
          {SpeechRecognitionAPI && (
            <button
              type="button"
              onClick={toggleVoiceInput}
              disabled={disabled}
              className={`shrink-0 h-[38px] w-9 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                isListening
                  ? "text-destructive hover:text-destructive/80 animate-pulse"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="voice-input-button"
              aria-label={isListening ? "Stop voice input" : "Start voice input"}
              aria-pressed={isListening}
              title={isListening ? "Stop voice input" : "Dictate a message (voice input)"}
            >
              {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}

          <div className="relative flex-1">
            <Textarea
              ref={textareaRef}
              value={text}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={placeholder}
              disabled={disabled}
              rows={3}
              className="resize-none w-full min-h-[72px] max-h-[200px] overflow-y-auto pr-9"
              style={resizable ? { height: textareaHeight } : undefined}
              data-testid="mention-input"
            />

            {/* Emoji picker button */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="absolute right-2 bottom-2 h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="emoji-picker-trigger"
                  tabIndex={-1}
                >
                  <Smile className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] overflow-hidden p-0 border-0 shadow-xl" side="top" align="end">
                <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading…</div>}>
                  <EmojiPicker onSelect={insertEmoji} />
                </Suspense>
              </PopoverContent>
            </Popover>
          </div>

          {/* Send button */}
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || (!text.trim() && stagedFiles.length === 0)}
            className="shrink-0 h-11 w-11 sm:h-[38px] sm:w-9 flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="send-button"
          >
            <Send className="h-5 w-5 sm:h-4 sm:w-4" />
          </button>
        </div>
      </div>

      {/* Autocomplete dropdown */}
      {showAutocomplete && totalAutocompleteCount > 0 && (
        <div
          className="absolute bottom-full left-0 mb-1 w-72 bg-popover border rounded-md shadow-lg z-50 py-1 max-h-56 overflow-y-auto"
          data-testid="mention-autocomplete"
        >
          {/* People & Agents section */}
          {filteredUsers.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                People &amp; Agents
              </div>
              {filteredUsers.map((user, idx) => (
                <button
                  key={user.userId}
                  type="button"
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-accent ${
                    idx === selectedIndex ? 'bg-accent' : ''
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMention(user);
                  }}
                  data-testid={`mention-option-${user.userId}`}
                >
                  <span className="truncate flex-1">{user.name}</span>
                  {user.isBot && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                      <Bot className="h-3 w-3" />
                      Bot
                    </span>
                  )}
                  {user.subtitle && (
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{user.subtitle}</span>
                  )}
                </button>
              ))}
            </>
          )}

          {/* Claims section */}
          {claimResults.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                Claims
              </div>
              {claimResults.map((claim, idx) => {
                const globalIdx = filteredUsers.length + idx;
                const name = [claim.policyholderFirstName, claim.policyholderLastName]
                  .filter(Boolean)
                  .join(' ') || `Claim #${claim.claimNumber}`;
                return (
                  <button
                    key={claim.id}
                    type="button"
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-accent ${
                      globalIdx === selectedIndex ? 'bg-accent' : ''
                    }`}
                    onMouseDown={(e) => { e.preventDefault(); insertClaimMention(claim); }}
                    data-testid={`claim-mention-option-${claim.id}`}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate flex-1">{name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      #{claim.claimNumber}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
